import { Controller } from "@hotwired/stimulus"

/**
 * Smart floating nav + mobile overlay.
 *
 * The controller lives on a wrapper div that contains both
 * the <header> (navTarget) and the mobile overlay (overlayTarget).
 */
export default class extends Controller {
  static targets = ["nav", "overlay", "hamburger", "spacer"]

  connect() {
    this.lastScrollY = window.scrollY
    this.isFloating = false

    if (this.hasNavTarget) {
      this.navHeight = this.navTarget.offsetHeight
      this.floatThreshold = this.navHeight * 2
    }

    this.handleScroll = this.handleScroll.bind(this)
    window.addEventListener("scroll", this.handleScroll, { passive: true })
  }

  disconnect() {
    window.removeEventListener("scroll", this.handleScroll)
    if (this.hasOverlayTarget && this.overlayTarget.open) this.overlayTarget.close()
    document.body.style.overflow = ""
  }

  // ── Floating nav ────────────────────────────────────────

  handleScroll() {
    if (!this.hasNavTarget) return

    const currentScrollY = window.scrollY
    const scrollingUp = currentScrollY < this.lastScrollY

    if (currentScrollY <= this.floatThreshold) {
      this.resetToTop()
    } else if (scrollingUp) {
      this.showFloating()
    } else {
      this.hideFloating()
    }

    this.lastScrollY = currentScrollY
  }

  showFloating() {
    if (this.isFloating) return
    this.isFloating = true

    const nav = this.navTarget

    // Spacer fills the gap left by the header leaving document flow
    if (this.hasSpacerTarget) this.spacerTarget.classList.remove("hidden")

    nav.style.position = "fixed"
    nav.style.top = "0"
    nav.style.left = "0"
    nav.style.right = "0"

    nav.classList.add("public-nav-floating")

    nav.style.transform = "translateY(-100%)"
    nav.offsetHeight // force reflow
    nav.style.transform = "translateY(0)"
  }

  hideFloating() {
    if (!this.isFloating) return
    this.isFloating = false

    const nav = this.navTarget
    nav.style.transform = "translateY(-100%)"

    setTimeout(() => {
      if (this.isFloating) return

      nav.style.position = ""
      nav.style.top = ""
      nav.style.left = ""
      nav.style.right = ""
      nav.style.transform = ""

      if (this.hasSpacerTarget) this.spacerTarget.classList.add("hidden")

      nav.classList.remove("public-nav-floating")
    }, 300)
  }

  // Instant swap back to in-flow when scrolled to the header's natural position
  resetToTop() {
    if (!this.isFloating) return
    this.isFloating = false

    const nav = this.navTarget

    nav.style.position = ""
    nav.style.top = ""
    nav.style.left = ""
    nav.style.right = ""
    nav.style.transform = ""

    if (this.hasSpacerTarget) this.spacerTarget.classList.add("hidden")

    nav.classList.remove("public-nav-floating")
  }

  // ── Mobile overlay ──────────────────────────────────────

  toggleOverlay() {
    if (!this.hasOverlayTarget) return

    if (this.overlayTarget.open) {
      this.closeOverlay()
    } else {
      this.openOverlay()
    }
  }

  openOverlay() {
    this.overlayTarget.showModal()
    document.body.style.overflow = "hidden"

    if (this.hasHamburgerTarget) {
      this.hamburgerTarget.setAttribute("aria-expanded", "true")
    }
  }

  closeOverlay(event) {
    event?.preventDefault()
    this.overlayTarget.close()
  }

  overlayClosed() {
    document.body.style.overflow = ""

    if (this.hasHamburgerTarget) {
      this.hamburgerTarget.setAttribute("aria-expanded", "false")
    }
  }

  closeOnLink() {
    this.closeOverlay()
  }

}
